import React from 'react'
import { FileButton } from '@e3dc-react/shell/Elements/Buttons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { makeStyles } from 'tss-react/mui'
import { downloadInstallationStatementFile, fetchInstallationStatementFileLink } from '../../api'
import { InstallationStatementLanguage, InstallationStatementLanguageType } from '../../types'
import { useIsWebviewMode } from 'src/Hooks'

const useStyles = makeStyles()(() => ({
  fileButton: {
    width: 'fit-content',
    height: 36,
    maxHeight: 36,
    flex: 1,
    minWidth: 0,
    marginRight: 0,
    maxWidth: 'fit-content',
  },
}))

/**
 * Button to download installation statement pdf file
 */
const InstallationStatementDownloadButton: React.FC = () => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()
  const { enqueueErrorStack } = useSnackbar()
  const { classes } = useStyles()
  const isWebview = useIsWebviewMode()

  const menuItems = [
    {
      label: t('common.languages.german'),
      onClick: () => handleDownload(InstallationStatementLanguage.GERMAN),
    },
    {
      label: t('common.languages.english'),
      onClick: () => handleDownload(InstallationStatementLanguage.ENGLISH),
    },
    {
      label: t('common.languages.dutch'),
      onClick: () => handleDownload(InstallationStatementLanguage.DUTCH),
    },
  ]

  const handleDownload = async (language: InstallationStatementLanguageType): Promise<void> => {
    try {
      const { hasErrors, value } = await fetchInstallationStatementFileLink(authFetch, language)
      if (hasErrors || !value) {
        enqueueErrorStack(t('installation-statement.download-button.error'))
      }
      if (value) {
        const { installationStatementsFormDownloadLink } = value
        const filename = t('installation-statement.download-button.filename')
        downloadInstallationStatementFile(installationStatementsFormDownloadLink, filename, isWebview)
      }
    } catch {
      enqueueErrorStack(t('installation-statement.download-button.error'))
    }
  }

  return (
    <FileButton
      name={t('installation-statement.download-button.title')}
      ext={'pdf'}
      fullWidth={true}
      fileClassName={classes.fileButton}
      apiEndpoint={process.env.REACT_APP_API_ENDPOINT ?? ''}
      menuItems={menuItems}
      openMenuOnButtonClick={true}
      mimeType="application/pdf"
      isWebview={isWebview}
    />
  )
}

export default InstallationStatementDownloadButton
